import {
  Body1Strong,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';

export interface ApiErrorMessageProps {
  /** Whatever the failed call's `catch` block caught — typically an `ApiError`. */
  error: unknown;
  /** The screen-specific title for the generic (5xx / unknown) case, e.g. "Could not load your requests". */
  title: string;
  /** Wired to the failed call's own retry, shown only for the generic case — 401/403 have their own action instead. */
  onRetry?: () => void;
  /** Where "Back to my requests" navigates on a 403. Defaults to the Dashboard. */
  backTo?: string;
}

function statusOf(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined;
}

/**
 * `useNavigate()` throws outside a `<Router>`. Isolating it in its own
 * component — mounted only for the 403 branch — means a screen with no
 * status-403 error in its tests (every one today: existing fixtures reject
 * with a plain `Error`, never a real `ApiError`) never needs a Router wrapper
 * just to render its generic or 401 error state.
 */
function BackToRequestsButton({ backTo }: { backTo: string }) {
  const navigate = useNavigate();
  return (
    <Button appearance="primary" onClick={() => void navigate(backTo)}>
      Back to my requests
    </Button>
  );
}

/**
 * Maps a caught API error's HTTP status to the wireframe's copy (sheet 08.2
 * E3, sheet S3b) instead of the raw `` `API GET /path → 500: {...}` ``
 * string `client.ts` throws (#154). A non-technical requester reading a
 * stack-trace-adjacent string is alarming and unhelpful — the wireframe calls
 * this "load-bearing copy, not filler."
 *
 * - **401** — the SSO token has expired; nothing short of a fresh tab load
 *   gets a new one, so the action is Reload, not Retry.
 * - **403** — access denied for *this* request (the realistic trigger is a
 *   stale notification deep-link to a request the viewer can no longer see);
 *   retrying the same call fails the same way, so the action is back to a
 *   screen the viewer does have access to.
 * - Anything else (5xx, a network failure, a non-`ApiError` rejection) — the
 *   generic E3 copy. "Your requests are safe" is the one sentence the
 *   wireframe calls out by name; nothing here is destructive, so it is
 *   always true.
 */
export function ApiErrorMessage({ error, title, onRetry, backTo = '/' }: ApiErrorMessageProps) {
  const status = statusOf(error);

  if (status === 401) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Your session expired</MessageBarTitle>
          Reload the tab to sign in again.
        </MessageBarBody>
        <MessageBarActions>
          <Button appearance="primary" onClick={() => globalThis.location.reload()}>
            Reload
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  }

  if (status === 403) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>You do not have access to this request</MessageBarTitle>
          It may have been reassigned, or you may no longer be in the right group.
        </MessageBarBody>
        <MessageBarActions>
          <BackToRequestsButton backTo={backTo} />
        </MessageBarActions>
      </MessageBar>
    );
  }

  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        Something went wrong on our side. <Body1Strong>Your requests are safe.</Body1Strong>
      </MessageBarBody>
      {onRetry && (
        <MessageBarActions>
          <Button appearance="primary" onClick={onRetry}>
            Try again
          </Button>
        </MessageBarActions>
      )}
    </MessageBar>
  );
}
